<script setup lang="ts">
import './assets/style.scss'
import { useModel } from '../model/model.ts'
import { useConfirm, useNotification, VIcons, VPreloader } from '@/shared/ui'
import { useTranslate } from '@/shared/lib'
import type { Ref } from 'vue'
import { inject, onBeforeUnmount, ref, watch, type PropType } from 'vue'
import { debounce } from 'lodash'

const props = defineProps({
  objectId: {
    type: Number,
    default: 0,
  },
  defaultOpen: {
    type: Boolean,
    default: false,
  },
  /** Create V2 batch: edit staged coordinates locally instead of loading/saving via API. */
  draftMode: {
    type: Boolean,
    default: false,
  },
  /** Draft coordinates `[[lng, lat], ...]` when `draftMode` is true. */
  draftCoordinates: {
    type: Array as PropType<number[][]>,
    default: () => [],
  },
  fieldsLocked: {
    type: Boolean,
    default: false,
  },
})

const { openForm, coords, isPolygon, loadCoords, removeCoords, setUserCoords, addCoordsRow, sendCoords, compareCoords } =
  useModel()

openForm.value = props.defaultOpen

const coordsSync = inject<Ref<{ objectId: number; coords: any[] } | null>>('coords-sync')
const isUpdatingFromMap = ref(false)
const coordsFormUpdate =
  inject<Ref<{ objectId: number; coords: any[] } | null>>('coords-form-update')

const emitCoordsToMap = debounce(() => {
  if (!coordsFormUpdate) return

  const val = coords.value
  const valid = val.every(
    (c) =>
      Array.isArray(c) &&
      c.length >= 2 &&
      Number.isFinite(Number(c[0])) &&
      Number.isFinite(Number(c[1])),
  )
  if (!valid) return

  coordsFormUpdate.value = { objectId: props.objectId, coords: val }
}, 300)

if (!props.draftMode) {
  watch(
    () => coordsSync?.value,
    (val) => {
      if (val && val.objectId === props.objectId && openForm.value) {
        isUpdatingFromMap.value = true
        coords.value = val.coords.map((coord) => [...coord])
      }
    },
  )

  watch(
    coords,
    () => {
      if (isUpdatingFromMap.value) {
        isUpdatingFromMap.value = false
        return
      }
      if (openForm.value) {
        emitCoordsToMap()
      }
    },
    { deep: true },
  )
}

onBeforeUnmount(() => {
  if (!props.draftMode) {
    emitCoordsToMap.cancel()
  }
})

const { addSuccess } = useNotification()
const { show } = useConfirm()
const { trs } = useTranslate()

const emit = defineEmits<{
  update: []
  'update:draftCoordinates': [value: number[][]]
}>()

const { isPending } = props.draftMode
  ? { isPending: ref(false) }
  : loadCoords(props.objectId)

watch(
  () => props.draftCoordinates,
  (next) => {
    if (!props.draftMode) return
    const rows = (next ?? []).map((c) => [Number(c[0]), Number(c[1])])
    coords.value = rows.length ? rows : [[0, 0]]
    isPolygon.value = compareCoords()
  },
  { immediate: true, deep: true },
)

const updateHandler = () => {
  coords.value = coords.value.map((item) => [Number(item[0]), Number(item[1])])

  if (isPolygon.value && !compareCoords()) {
    coords.value[coords.value.length] = coords.value[0]
  }

  if (props.draftMode) {
    emit('update:draftCoordinates', coords.value.map((c) => [Number(c[0]), Number(c[1])]))
    addSuccess(trs('itf.coordinates_updated'))
    return
  }

  show({
    title: trs('itf.confirm_change_coords'),
    confirmFunc: () =>
      sendCoords(props.objectId).then(() => {
        addSuccess(trs('itf.coordinates_updated'))
        emit('update')
      }),
  })
}
</script>

<template>
  <div class="object-coords-manually__button-wrapper">
    <VSwitch
      v-model="openForm"
      :label="$t('itf.open_coords_manually_form')"
      density="compact"
      hide-details
      :disabled="fieldsLocked"
    />
  </div>

  <div class="object-coords-manually__form">
    <VForm v-if="openForm && !fieldsLocked">
      <VPreloader v-if="isPending" :wrapper-height="100" />

      <template v-else>
        <div class="object-coords-manually__row" v-for="(coord, index) in coords" :key="index">
          <VSpeedDial location="right center" transition="fade-transition">
            <template v-slot:activator="{ props }">
              <VBtn color="default" v-bind="props" icon density="compact">
                <VIcons type="more_vert" />
              </VBtn>
            </template>

            <VTooltip location="top" :text="$t('itf.archive')">
              <template #activator="{ props: tipProps }">
                <VBtn
                  key="1"
                  color="error"
                  icon
                  density="compact"
                  v-bind="tipProps"
                  @click="removeCoords(index)"
                >
                  <VIcons type="archive" />
                </VBtn>
              </template>
            </VTooltip>

            <VTooltip location="top" :text="$t('itf.coords_add_row_above')">
              <template #activator="{ props: tipProps }">
                <VBtn
                  key="2"
                  icon
                  density="compact"
                  v-bind="tipProps"
                  @click="addCoordsRow(index, 'up')"
                >
                  <VIcons type="add_row_above" />
                </VBtn>
              </template>
            </VTooltip>

            <VTooltip location="top" :text="$t('itf.coords_add_row_below')">
              <template #activator="{ props: tipProps }">
                <VBtn
                  key="3"
                  icon
                  density="compact"
                  v-bind="tipProps"
                  @click="addCoordsRow(index, 'down')"
                >
                  <VIcons type="add_row_below" />
                </VBtn>
              </template>
            </VTooltip>

            <VTooltip location="top" :text="$t('itf.map_my_position')">
              <template #activator="{ props: tipProps }">
                <VBtn
                  key="4"
                  icon
                  density="compact"
                  v-bind="tipProps"
                  @click="setUserCoords(index)"
                >
                  <VIcons type="my_location" />
                </VBtn>
              </template>
            </VTooltip>
          </VSpeedDial>

          <VTextField :label="$t('itf.longitude')" v-model="coord[0]" hide-details />

          <VTextField :label="$t('itf.latitude')" v-model="coord[1]" hide-details />
        </div>

        <div class="object-coords-manually__buttons">
          <VBtn @click="updateHandler">
            {{ $t('itf.update_coords') }}
          </VBtn>

          <VBtn color="default" variant="tonal" @click="addCoordsRow(coords.length, 'down')">
            {{ $t('itf.add_row') }}
          </VBtn>
        </div>
      </template>
    </VForm>
  </div>
</template>
